import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm tracking-wide ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary-hover",
                destructive:
                    "bg-destructive text-destructive-foreground hover:bg-destructive-hover",
                outline:
                    "text-foreground border border-border bg-transparent hover:bg-primary hover:text-primary-foreground",
                secondary:
                    "bg-secondary text-secondary-foreground hover:bg-secondary-hover",
                ghost: "text-foreground hover:bg-primary hover:text-primary-foreground",
                subtle: "text-foreground bg-transparent hover:bg-hover",
                link: "text-primary underline-offset-4 hover:underline",
                light: "bg-primary/10 text-primary-foreground hover:bg-primary-hover/25",
                lightGrey: "text-foreground border border-border bg-transparent hover:bg-muted-foreground/5"
            },
            size: {
                default: "h-12 px-8 py-2",
                sm: "h-9 rounded-md px-6",
                lg: "h-11 rounded-md px-10",
                icon: "h-10 w-10",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button"
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        )
    }
)
Button.displayName = "Button"

export { Button, buttonVariants }
